import { get } from 'lodash';
import { pruneFieldErrors } from '@/utils/errors';
import findResult, {
  findWithCodes, hasKey, selectableResult,
} from '@/utils/resultsHelper';
import { formatDate, formatDateForState } from '@/utils/dateHelper';
import pagingWithFilteredResults from '@/constants/states';
import fields from '@/constants/fields';
import { tokenCategories, tokenStates } from '../constants';

const statusFilters = (activeFlag = true, expiredFlag = false, systemFlag = false) => ({
  activeFlag,
  expiredFlag,
  systemFlag,
});

const allTokens = (activeFlag, expiredFlag, systemFlag) => (
  {
    all: {
      ...pagingWithFilteredResults(),
      ...statusFilters(activeFlag, expiredFlag, systemFlag),
      ...tokenStates,
    },
  }
);

const adminTokens = (activeFlag, expiredFlag, systemFlag) => (
  {
    admin: {
      ...pagingWithFilteredResults(),
      ...statusFilters(activeFlag, expiredFlag, systemFlag),
      ...tokenStates,
    },
  }
);

export const activeToken = () => ({
  open: false,
  code: null,
  name: '',
  description: '',
  role: '',
  expiresOn: formatDateForState(new Date()),
  ip_range: '',
  max_uses: null,
  jwt: '',
  ...tokenStates,
});

const initialState = {
  deleteTokenOpen: false,
  deleteTokenCategory: null,
  disableTokenOpen: false,
  disableTokenCategory: null,
  activeToken: { ...activeToken() },
  tokens: {
    ...allTokens(true, false, false),
    ...adminTokens(true, false, false),
    batch: {
      results: [],
    },
  },
};

export const pruneTokenErrors = (errors, { properties }) => {
  let filteredErrors = errors;

  if (hasKey(properties, 'name')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.NAME);
  }
  if (hasKey(properties, 'code')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.CODE);
  }
  if (hasKey(properties, 'description')) {
    filteredErrors = pruneFieldErrors(filteredErrors, fields.DESCRIPTION);
  }

  return filteredErrors;
};

const tokensResult = (result, jwtCode, jwt) => ({
  ...selectableResult(result),
  createdOn: result.created_at ? formatDate(result.created_at, {
    month: 'long', year: 'numeric', day: 'numeric',
  }) : '',
  expiresOn: result.expires_at ? formatDate(result.expires_at, {
    month: 'long', year: 'numeric', day: 'numeric',
  }) : '',
  jwt: result.code === jwtCode && result.active ? jwt : null,
});

export const tokensResults = (existing, { results, jwtCode, jwt }) => (
  existing.concat(results.map((item) => tokensResult(item, jwtCode, jwt)))
);

export const newTokenProperties = (item, action) => ({
  category: action.category,
  open: true,
  token: item?.code || 'standard',
  ...tokenStates,
});

export const tokenStateToLoad = ({ tokens }) => {
  const items = get(tokens, 'tokens', initialState.tokens);
  const allFilters = items.all || initialState.tokens.all;
  const adminFilters = items.admin || initialState.tokens.admin;

  return {
    ...initialState,
    tokens: {
      ...initialState.tokens,
      ...allTokens(allFilters.activeFlag, allFilters.expiredFlag, allFilters.systemFlag),
      ...adminTokens(adminFilters.activeFlag, adminFilters.expiredFlag, adminFilters.systemFlag),
    },
  };
};

const flagsToSave = ({
  activeFlag, expiredFlag, systemFlag,
}) => ({
  activeFlag,
  expiredFlag,
  systemFlag,
});

export const tokenStateToSave = ({ tokens }) => (
  {
    tokens: {
      tokens: {
        all: flagsToSave(tokens.tokens.all),
        admin: flagsToSave(tokens.tokens.admin),
      },
    },
  }
);

export const findTokens = ({ tokens }, { category }) => tokens[category];

export const findAllTokens = ({ tokens }, { items }) => {
  const codes = items.map(({ code }) => code);
  return findWithCodes(tokens, tokenCategories, codes);
};

export const findToken = ({ tokens }, { category, code }) => findResult(tokens, category, code);

export default initialState;
